/**
 * @fileoverview Defines the column definitions for the Text entity grid.
 * @category Components/Shared/Text
 */

import { ColDef, ValueGetterParams, ValueFormatterParams } from 'ag-grid-enterprise';
import { Text } from '@models/DomainModels/Master/text.ts';
import { TextScope as TextScopeType } from '@models/DomainModels/Master/text-scope.ts';
import { TFunction } from 'i18next';
import { TextType } from '@models/enums/TextType';
import { TextScope } from '@models/enums/TextScope';

/**
 * Utility function to extract enum key from enum value.
 */
const getEnumKey = <T extends Record<string, unknown>>(enumObject: T, value: unknown): string | undefined => {
  return Object.entries(enumObject).find(([, val]) => val === value)?.[0];
};

/**
 * Translates an enum value using the provided translation function.
 */
const translateEnumValue = <T extends Record<string, unknown>>(enumObject: T, value: unknown, translationKey: string, t: TFunction): string => {
  const enumKey = getEnumKey(enumObject, value);
  return enumKey ? t(`${translationKey}.${enumKey}`) : String(value);
};

/**
 * Creates a value formatter for enum fields.
 */
const createEnumValueFormatter =
  <T extends Record<string, unknown>>(enumObject: T, translationKey: string, t: TFunction) =>
  (params: ValueFormatterParams<Text>) => {
    if (!params.value) return '';
    return translateEnumValue(enumObject, params.value, translationKey, t);
  };

/**
 * Creates a filter value getter for enum fields.
 */
const createEnumFilterValueGetter =
  <T extends Record<string, unknown>>(enumObject: T, translationKey: string, fieldKey: keyof Text, t: TFunction) =>
  (params: ValueGetterParams<Text>) => {
    const value = params.data?.[fieldKey];
    if (!value) return null;
    return translateEnumValue(enumObject, value, translationKey, t);
  };

/**
 * Renders an array of TextScope objects as a comma-separated string.
 */
const renderTextScopes = (scopes: TextScopeType[], t: TFunction): string => {
  return scopes.map(scope => translateEnumValue(TextScope, scope.scope, 'enums:textScope', t)).join(', ');
};

/**
 * Creates column definition for text scopes with proper filtering and rendering.
 */
const createTextScopesColumn = (t: TFunction): ColDef<Text> => {
  const allScopeValues = Object.entries(TextScope).map(([key]) => t(`enums:textScope.${key}`));

  return {
    headerName: t('headers.scopes'),
    field: 'textScopes',
    valueGetter: params => params.data?.textScopes ?? [],
    cellRenderer: (params: { value: TextScopeType[] }) => {
      if (!params.value || params.value.length === 0) return null;
      return renderTextScopes(params.value, t);
    },
    sortable: false,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: allScopeValues,
      suppressSorting: false,
      comparator: (a: string, b: string) => a.localeCompare(b)
    },
    resizable: true,
    flex: 2,
    editable: false,
    valueFormatter: params => {
      if (!params.value || params.value.length === 0) return '';
      return renderTextScopes(params.value, t);
    },
    filterValueGetter: (params: ValueGetterParams<Text>) => {
      if (!params.data?.textScopes || params.data.textScopes.length === 0) return null;
      return params.data.textScopes.map(scopeObj => translateEnumValue(TextScope, scopeObj.scope, 'enums:textScope', t));
    }
  };
};

/**
 * Creates a standard text column definition.
 */
const createTextColumn = (
  headerKey: string,
  field: keyof Text,
  flex: number,
  sortable: boolean = true,
  options: Partial<ColDef<Text>> = {}
): ColDef<Text> => ({
  headerName: headerKey,
  field,
  sortable,
  filter: true,
  resizable: true,
  flex,
  editable: false,
  ...options
});

/**
 * Creates a number column definition with proper formatting.
 */
const createNumberColumn = (headerKey: string, field: keyof Text, flex: number): ColDef<Text> => ({
  headerName: headerKey,
  field,
  sortable: true,
  filter: 'agSetColumnFilter',
  resizable: true,
  flex,
  editable: false,
  valueFormatter: params => {
    if (params.value === null || params.value === undefined) return '';
    return String(params.value);
  },
  filterValueGetter: (params: ValueGetterParams<Text>) => {
    const value = params.data?.[field];
    if (value === null || value === undefined) return null;
    return String(value);
  }
});

/**
 * Generates the column definitions for the Text grid.
 *
 * @param {{ t: TFunction }} params - Parameters including the translation function.
 * @returns {ColDef<Text>[]} An array of column definitions.
 * @category Components/Shared/Text
 */
export const gridColumnDefinitions = ({ t }: { readonly t: TFunction }): ColDef<Text>[] => {
  const columns: ColDef<Text>[] = [
    createNumberColumn(t('headers.number'), 'number', 1),

    createTextColumn(t('headers.name'), 'name', 1.5, true),

    {
      ...createTextColumn(t('headers.type'), 'type', 1, true, {
        filter: 'agSetColumnFilter'
      }),
      valueFormatter: createEnumValueFormatter(TextType, 'enums:textType', t),
      filterValueGetter: createEnumFilterValueGetter(TextType, 'enums:textType', 'type', t)
    },

    createTextScopesColumn(t),

    createTextColumn(t('headers.value'), 'value', 6, false)
  ];

  return columns;
};
